/**
 * ========================================
 * Sensor Data Grid Component
 * ========================================
 * 
 * 측정자료 조회 및 다운로드 컴포넌트
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender } from '@tanstack/react-table';
import { motion } from 'framer-motion';
import { HiDownload, HiRefresh, HiSearch, HiX } from 'react-icons/hi';
import { sensorDataAPI, spotAPI } from '../services/api.service';
import { format, subDays } from 'date-fns';

// SHA-256 해시 계산 함수
async function calculateSHA256(text) {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

export default function SensorDataGrid() {
  // 기본 날짜 설정: 시작일 = 오늘-7일, 종료일 = 오늘
  const getDefaultStartDate = () => {
    return format(subDays(new Date(), 7), 'yyyy-MM-dd');
  };
  
  const getDefaultEndDate = () => {
    return format(new Date(), 'yyyy-MM-dd');
  };

  const [selectedSpotId, setSelectedSpotId] = useState('all');
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [processedData, setProcessedData] = useState([]);
  const [isProcessingHash, setIsProcessingHash] = useState(false);
  const [spotSearchQuery, setSpotSearchQuery] = useState('');
  const [isSpotDropdownOpen, setIsSpotDropdownOpen] = useState(false);
  const spotDropdownRef = useRef(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // 스팟 목록 조회
  const { data: spotsData } = useQuery({
    queryKey: ['spots'],
    queryFn: () => spotAPI.getSpots({ limit: 100 }),
  });

  const spots = spotsData?.data?.spots || [];

  // 스팟 검색 필터링
  const filteredSpots = useMemo(() => {
    if (!spotSearchQuery.trim()) {
      return spots;
    }
    const query = spotSearchQuery.toLowerCase();
    return spots.filter(spot => 
      spot.spot_name.toLowerCase().includes(query)
    );
  }, [spots, spotSearchQuery]);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (spotDropdownRef.current && !spotDropdownRef.current.contains(event.target)) {
        setIsSpotDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 센서 데이터 조회 (서버 사이드 페이징)
  const { data: sensorDataResponse, isLoading, refetch } = useQuery({
    queryKey: ['sensorData', selectedSpotId, startDate, endDate, pageIndex, pageSize],
    queryFn: async () => {
      const params = {
        limit: pageSize,
        offset: pageIndex * pageSize,
        order_by: 'id',
        order_direction: 'desc', // ID 내림차순 정렬
      };
      
      if (selectedSpotId !== 'all') {
        // 스팟 목록에서 선택한 값이 UUID인지 ID인지 확인
        const selectedSpot = spots.find(s => (s.id || s.uuid) == selectedSpotId);
        if (selectedSpot) {
          // UUID가 있으면 spot_uuid 사용, 없으면 spot_id 사용
          if (selectedSpot.uuid) {
            params.spot_uuid = selectedSpot.uuid;
          } else {
            params.spot_id = selectedSpot.id || selectedSpotId;
          }
        } else {
          // 찾지 못한 경우 그대로 전송 (백엔드에서 처리)
          params.spot_id = selectedSpotId;
        }
      }
      
      if (startDate) {
        params.from_date = startDate;
      }
      
      if (endDate) {
        params.to_date = endDate;
      }

      const response = await sensorDataAPI.getSensorData(params);
      return response.data || { sensor_data: [], total_count: 0 };
    },
    enabled: !!startDate && !!endDate, // 시작일과 종료일이 모두 있을 때만 조회
  });

  const sensorData = sensorDataResponse?.sensor_data || [];
  
  // 총 개수 업데이트
  useEffect(() => {
    if (sensorDataResponse?.total_count !== undefined) {
      setTotalCount(sensorDataResponse.total_count);
    }
  }, [sensorDataResponse]);

  // 페이지 변경 시 첫 페이지로 리셋
  useEffect(() => {
    setPageIndex(0);
  }, [selectedSpotId, startDate, endDate]);

  // 데이터 변환 및 해시 계산 (비동기 처리)
  useEffect(() => {
    if (!sensorData || sensorData.length === 0) {
      setProcessedData([]);
      return;
    }

    setIsProcessingHash(true);
    
    // 각 레코드에 대해 SHA-256 해시 계산
    Promise.all(
      sensorData.map(async (row) => {
        const recordString = JSON.stringify({
          id: row.id,
          timestamp: row.sensor_timestamp,
          latitude: row.latitude,
          longitude: row.longitude,
          total_field_nt: row.total_field_nt,
        });
        
        // SHA-256 해시 계산
        const dataIntegrityHash = await calculateSHA256(recordString);
        
        // Audit log 해시 (전체 레코드 정보 포함)
        const auditRecordString = JSON.stringify({
          ...row,
          record_uuid: row.id || `REC-${row.id}`,
        });
        const auditLogHash = await calculateSHA256(auditRecordString);

        return {
          record_uuid: `REC-${row.id}`,
          created_timestamp_utc: row.created_at || row.sensor_timestamp,
          measurement_latitude: row.latitude,
          measurement_longitude: row.longitude,
          device_sensor_id: row.device_model || 'UNKNOWN',
          os_version: row.os_version || '',
          sensor_vendor: row.sensor_vendor || '',
          original_value: row.total_field_nt,
          original_value_ut: row.total_field_ut || (row.total_field_nt / 1000),
          corrected_value: row.total_field_nt, // 변경 없으므로 원본값
          grade: row.grade || null,
          grade_display: row.grade_display || null,
          is_over_range: row.is_over_range || false,
          is_environment_suspected: row.is_environment_suspected || false,
          quality_flags: row.quality_flags ? (typeof row.quality_flags === 'string' ? JSON.parse(row.quality_flags) : row.quality_flags) : {},
          change_actor: '시스템',
          change_timestamp: row.sensor_timestamp || row.created_at,
          change_reason: '없음',
          change_version: 1,
          data_integrity_hash: dataIntegrityHash,
          audit_log_hash: auditLogHash,
        };
      })
    ).then((processed) => {
      setProcessedData(processed);
      setIsProcessingHash(false);
    }).catch((error) => {
      console.error('Hash calculation error:', error);
      setIsProcessingHash(false);
      // 오류 발생 시 해시 없이 처리
        setProcessedData(
        sensorData.map((row) => ({
          record_uuid: `REC-${row.id}`,
          created_timestamp_utc: row.created_at || row.sensor_timestamp,
          measurement_latitude: row.latitude,
          measurement_longitude: row.longitude,
          device_sensor_id: row.device_model || 'UNKNOWN',
          os_version: row.os_version || '',
          sensor_vendor: row.sensor_vendor || '',
          original_value: row.total_field_nt,
          original_value_ut: row.total_field_ut || (row.total_field_nt / 1000),
          corrected_value: row.total_field_nt,
          grade: row.grade || null,
          grade_display: row.grade_display || null,
          is_over_range: row.is_over_range || false,
          is_environment_suspected: row.is_environment_suspected || false,
          quality_flags: row.quality_flags ? (typeof row.quality_flags === 'string' ? JSON.parse(row.quality_flags) : row.quality_flags) : {},
          change_actor: '시스템',
          change_timestamp: row.sensor_timestamp || row.created_at,
          change_reason: '없음',
          change_version: 1,
          data_integrity_hash: 'ERROR',
          audit_log_hash: 'ERROR',
        }))
      );
    });
  }, [sensorData]);

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      {
        accessorKey: 'record_uuid',
        header: 'Record UUID',
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'created_timestamp_utc',
        header: '생성 시점 (UTC)',
        size: 180,
        minSize: 180,
        maxSize: 180,
        cell: (info) => {
          const value = info.getValue();
          if (!value) return '-';
          
          // DB에서 조회된 UTC 시간을 보기 편한 형식으로 표시
          try {
            const date = new Date(value);
            // 유효한 날짜인지 확인
            if (isNaN(date.getTime())) {
              // 유효하지 않은 날짜면 원본 값 반환
              return String(value);
            }
            // UTC 시간을 yyyy-MM-dd HH:mm:ss 형식으로 표시
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            const hours = String(date.getUTCHours()).padStart(2, '0');
            const minutes = String(date.getUTCMinutes()).padStart(2, '0');
            const seconds = String(date.getUTCSeconds()).padStart(2, '0');
            
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
          } catch (e) {
            // 파싱 실패 시 원본 값 반환
            return String(value);
          }
        },
      },
      {
        accessorKey: 'measurement_latitude',
        header: '위도',
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: (info) => parseFloat(info.getValue()).toFixed(6),
      },
      {
        accessorKey: 'measurement_longitude',
        header: '경도',
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: (info) => parseFloat(info.getValue()).toFixed(6),
      },
      {
        accessorKey: 'device_sensor_id',
        header: '기기/센서 ID',
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'os_version',
        header: 'OS 버전',
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: (info) => info.getValue() || '-',
      },
      {
        accessorKey: 'sensor_vendor',
        header: '센서 제조사',
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: (info) => info.getValue() || '-',
      },
      {
        accessorKey: 'original_value_ut',
        header: '측정값 (µT)',
        size: 110,
        minSize: 110,
        maxSize: 110,
        cell: (info) => {
          const value = info.getValue();
          const row = info.row.original;
          // UX 표시 상한 체크 (120 µT)
          if (value > 120) {
            return '120+';
          }
          return parseFloat(value).toFixed(2);
        },
      },
      {
        accessorKey: 'grade_display',
        header: '등급',
        size: 100,
        minSize: 100,
        maxSize: 150,
        cell: (info) => {
          const grade = info.getValue();
          const row = info.row.original;
          if (!grade) return '-';
          
          let badgeClass = 'px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap';
          if (row.grade === 'weak') badgeClass += ' bg-blue-500/20 text-blue-400';
          else if (row.grade === 'medium') badgeClass += ' bg-green-500/20 text-green-400';
          else if (row.grade === 'strong') badgeClass += ' bg-yellow-500/20 text-yellow-400';
          else if (row.grade === 'very_strong') badgeClass += ' bg-orange-500/20 text-orange-400';
          else if (row.grade === 'extreme') badgeClass += ' bg-red-500/20 text-red-400';
          else badgeClass += ' bg-gray-500/20 text-gray-400';
          
          return (
            <span className={badgeClass}>
              {grade}
            </span>
          );
        },
      },
      {
        accessorKey: 'quality_flags',
        header: '품질 플래그',
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: (info) => {
          const flags = info.getValue();
          const row = info.row.original;
          const flagList = [];
          
          if (row.is_over_range) {
            flagList.push(<span key="over_range" className="px-2 py-1 text-xs font-semibold bg-red-500/20 text-red-400 rounded mr-1">측정한계</span>);
          }
          if (row.is_environment_suspected) {
            flagList.push(<span key="env" className="px-2 py-1 text-xs font-semibold bg-yellow-500/20 text-yellow-400 rounded mr-1">환경영향</span>);
          }
          if (flags && Object.keys(flags).length > 0) {
            if (flags.rapid_change) {
              flagList.push(<span key="rapid" className="px-2 py-1 text-xs font-semibold bg-orange-500/20 text-orange-400 rounded mr-1">급격변동</span>);
            }
          }
          
          return flagList.length > 0 ? <div className="flex flex-wrap gap-1 justify-center">{flagList}</div> : <div className="text-center">-</div>;
        },
      },
      {
        accessorKey: 'original_value',
        header: '원본 측정값 (nT)',
        size: 140,
        minSize: 140,
        maxSize: 140,
        cell: (info) => parseFloat(info.getValue()).toFixed(2),
      },
      {
        accessorKey: 'corrected_value',
        header: '보정/산출 후 값 (nT)',
        size: 150,
        minSize: 150,
        maxSize: 150,
        cell: (info) => parseFloat(info.getValue()).toFixed(2),
      },
      {
        accessorKey: 'change_actor',
        header: '변경 주체',
        size: 100,
        minSize: 100,
        maxSize: 100,
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'change_timestamp',
        header: '변경 시점 (UTC)',
        size: 180,
        minSize: 180,
        maxSize: 180,
        cell: (info) => {
          const value = info.getValue();
          if (!value) return '-';
          
          // DB에서 조회된 UTC 시간을 보기 편한 형식으로 표시
          try {
            const date = new Date(value);
            // 유효한 날짜인지 확인
            if (isNaN(date.getTime())) {
              // 유효하지 않은 날짜면 원본 값 반환
              return String(value);
            }
            // UTC 시간을 yyyy-MM-dd HH:mm:ss 형식으로 표시
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            const hours = String(date.getUTCHours()).padStart(2, '0');
            const minutes = String(date.getUTCMinutes()).padStart(2, '0');
            const seconds = String(date.getUTCSeconds()).padStart(2, '0');
            
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
          } catch (e) {
            // 파싱 실패 시 원본 값 반환
            return String(value);
          }
        },
      },
      {
        accessorKey: 'change_reason',
        header: '변경 사유',
        size: 100,
        minSize: 100,
        maxSize: 100,
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'change_version',
        header: '변경 버전',
        size: 100,
        minSize: 100,
        maxSize: 100,
        cell: (info) => info.getValue(),
      },
    ],
    []
  );

  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true, // 서버 사이드 페이징
    pageCount: Math.ceil(totalCount / pageSize),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: pageSize,
      },
    },
    state: {
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' 
        ? updater({ pageIndex, pageSize })
        : updater;
      setPageIndex(newPagination.pageIndex);
      setPageSize(newPagination.pageSize);
    },
  });

  // CSV 값 이스케이프 함수 (쉼표, 줄바꿈, 따옴표 처리)
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    // 쉼표, 줄바꿈, 따옴표가 포함된 경우 따옴표로 감싸고 내부 따옴표는 두 개로 변환
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // UTC 시간 포맷팅 함수
  const formatUTCForCSV = (value) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return String(value);
      }
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
    } catch (e) {
      return String(value);
    }
  };

  // CSV 다운로드 (전체 데이터 조회)
  const handleDownloadCSV = async () => {
    try {
      console.log('CSV 다운로드 시작: 전체 데이터 조회 중...');
      
      // 전체 데이터 조회를 위한 파라미터 설정
      const params = {
        limit: 1000000, // 매우 큰 값으로 설정하여 모든 데이터 조회
        offset: 0,
        order_by: 'id',
        order_direction: 'desc', // ID 내림차순 정렬
      };
      
      if (selectedSpotId !== 'all') {
        const selectedSpot = spots.find(s => (s.id || s.uuid) == selectedSpotId);
        if (selectedSpot) {
          if (selectedSpot.uuid) {
            params.spot_uuid = selectedSpot.uuid;
          } else {
            params.spot_id = selectedSpot.id || selectedSpotId;
          }
        } else {
          params.spot_id = selectedSpotId;
        }
      }
      
      if (startDate) {
        params.from_date = startDate;
      }
      
      if (endDate) {
        params.to_date = endDate;
      }

      // 전체 데이터 조회
      const response = await sensorDataAPI.getSensorData(params);
      const allSensorData = response.data?.sensor_data || [];
      
      if (!allSensorData.length) {
        alert('다운로드할 데이터가 없습니다.');
        return;
      }

      // 전체 데이터 처리 및 해시 계산
      setIsProcessingHash(true);
      
      const allProcessedData = await Promise.all(
        allSensorData.map(async (row) => {
          const recordString = JSON.stringify({
            id: row.id,
            timestamp: row.sensor_timestamp,
            latitude: row.latitude,
            longitude: row.longitude,
            total_field_nt: row.total_field_nt,
          });
          
          const dataIntegrityHash = await calculateSHA256(recordString);
          
          const auditRecordString = JSON.stringify({
            ...row,
            record_uuid: row.id || `REC-${row.id}`,
          });
          const auditLogHash = await calculateSHA256(auditRecordString);

          return {
            record_uuid: `REC-${row.id}`,
            created_timestamp_utc: row.created_at || row.sensor_timestamp,
            measurement_latitude: row.latitude,
            measurement_longitude: row.longitude,
            device_sensor_id: row.device_model || 'UNKNOWN',
            os_version: row.os_version || '',
            sensor_vendor: row.sensor_vendor || '',
            original_value: row.total_field_nt,
            original_value_ut: row.total_field_ut || (row.total_field_nt / 1000),
            corrected_value: row.total_field_nt,
            grade: row.grade || null,
            grade_display: row.grade_display || null,
            is_over_range: row.is_over_range || false,
            is_environment_suspected: row.is_environment_suspected || false,
            quality_flags: row.quality_flags ? (typeof row.quality_flags === 'string' ? JSON.parse(row.quality_flags) : row.quality_flags) : {},
            change_actor: '시스템',
            change_timestamp: row.sensor_timestamp || row.created_at,
            change_reason: '없음',
            change_version: 1,
            data_integrity_hash: dataIntegrityHash,
            audit_log_hash: auditLogHash,
          };
        })
      );
      
      setIsProcessingHash(false);

      // CSV 헤더
      const headers = [
        'Record UUID',
        '생성 시점 (UTC)',
        '위도',
        '경도',
        '기기/센서 ID',
        'OS 버전',
        '센서 제조사',
        '측정값 (µT)',
        '등급',
        '품질 플래그',
        '원본 측정값 (nT)',
        '보정/산출 후 값 (nT)',
        '변경 주체',
        '변경 시점 (UTC)',
        '변경 사유',
        '변경 버전',
        '무결성 해시값',
        'Audit Log 해시',
      ];

      // CSV 데이터 행 생성
      const csvRows = [
        headers.map(escapeCSVValue).join(','),
        ...allProcessedData.map((row) => {
          const qualityFlags = [];
          if (row.is_over_range) qualityFlags.push('측정한계');
          if (row.is_environment_suspected) qualityFlags.push('환경영향');
          if (row.quality_flags?.rapid_change) qualityFlags.push('급격변동');
          
          const values = [
            row.record_uuid || '',
            formatUTCForCSV(row.created_timestamp_utc),
            row.measurement_latitude != null ? parseFloat(row.measurement_latitude).toFixed(6) : '',
            row.measurement_longitude != null ? parseFloat(row.measurement_longitude).toFixed(6) : '',
            row.device_sensor_id || '',
            row.os_version || '',
            row.sensor_vendor || '',
            row.original_value_ut != null 
              ? (row.original_value_ut > 120 ? '120+' : parseFloat(row.original_value_ut).toFixed(2))
              : '',
            row.grade_display || '',
            qualityFlags.join(';'),
            row.original_value != null ? parseFloat(row.original_value).toFixed(2) : '',
            row.corrected_value != null ? parseFloat(row.corrected_value).toFixed(2) : '',
            row.change_actor || '',
            formatUTCForCSV(row.change_timestamp),
            row.change_reason || '',
            row.change_version != null ? String(row.change_version) : '',
            row.data_integrity_hash || '',
            row.audit_log_hash || '',
          ];
          
          return values.map(escapeCSVValue).join(',');
        }),
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
      link.setAttribute('download', `sensor_data_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // URL 객체 정리 (메모리 누수 방지)
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);

      // JSON 메타데이터 생성 (비동기로 SHA-256 계산)
      try {
        const allDataString = JSON.stringify(allProcessedData);
        const dataIntegrityHash = await calculateSHA256(allDataString);
        
        const metadata = {
          export_timestamp: new Date().toISOString(),
          total_records: allProcessedData.length,
          filter: {
            spot_id: selectedSpotId,
            start_date: startDate,
            end_date: endDate,
          },
          audit_log: {
            export_user: 'admin',
            export_type: 'CSV',
            data_integrity_hash: dataIntegrityHash,
            hash_algorithm: 'SHA-256',
          },
        };

        const metadataBlob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
        const metadataLink = document.createElement('a');
        const metadataUrl = URL.createObjectURL(metadataBlob);
        metadataLink.setAttribute('href', metadataUrl);
        metadataLink.setAttribute('download', `sensor_data_metadata_${timestamp}.json`);
        metadataLink.style.visibility = 'hidden';
        document.body.appendChild(metadataLink);
        metadataLink.click();
        document.body.removeChild(metadataLink);
        
        // 메타데이터 URL 객체 정리
        setTimeout(() => {
          URL.revokeObjectURL(metadataUrl);
        }, 100);
      } catch (error) {
        console.error('Metadata generation error:', error);
        alert('메타데이터 생성 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('CSV 다운로드 오류:', error);
      alert('CSV 다운로드 중 오류가 발생했습니다: ' + error.message);
    }
  };


  return (
    <div className="space-y-6">
      {/* 조회 영역 */}
      <motion.div
        className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl border border-theme-primary/20 p-6 relative z-10"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col md:flex-row gap-4 items-end">
          {/* 스팟 선택 (검색 가능한 드롭다운) */}
          <div className="flex-1 min-w-[200px] relative z-50" ref={spotDropdownRef}>
            <label className="block text-sm font-semibold text-theme-text-secondary mb-2">
              스팟 선택
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsSpotDropdownOpen(!isSpotDropdownOpen)}
                className="w-full px-4 py-2.5 bg-theme-background border-2 border-theme-primary/30 rounded-xl text-sm font-medium text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all shadow-sm hover:shadow-md flex items-center justify-between"
              >
                <span>
                  {selectedSpotId === 'all' 
                    ? '전체' 
                    : spots.find(s => (s.id || s.uuid) == selectedSpotId)?.spot_name || '선택하세요'}
                </span>
                <HiSearch className="w-4 h-4 text-theme-text-secondary" />
              </button>
              
              {isSpotDropdownOpen && (
                <motion.div
                  className="absolute z-[100] w-full mt-2 bg-theme-surface border-2 border-theme-primary/30 rounded-xl shadow-2xl max-h-60 overflow-hidden"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  style={{ position: 'absolute', top: '100%' }}
                >
                  {/* 검색 입력 */}
                  <div className="p-3 border-b border-theme-primary/20">
                    <div className="relative">
                      <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-theme-text-secondary" />
                      <input
                        type="text"
                        value={spotSearchQuery}
                        onChange={(e) => setSpotSearchQuery(e.target.value)}
                        placeholder="스팟명 검색..."
                        className="w-full pl-10 pr-8 py-2 bg-theme-background border border-theme-primary/30 rounded-lg text-sm text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary"
                        autoFocus
                      />
                      {spotSearchQuery && (
                        <button
                          onClick={() => setSpotSearchQuery('')}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-theme-text-secondary hover:text-theme-text"
                        >
                          <HiX className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* 스팟 목록 */}
                  <div className="max-h-48 overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedSpotId('all');
                        setIsSpotDropdownOpen(false);
                        setSpotSearchQuery('');
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                        selectedSpotId === 'all'
                          ? 'bg-theme-primary/20 text-theme-primary font-semibold'
                          : 'text-theme-text hover:bg-theme-surface/50'
                      }`}
                    >
                      전체
                    </button>
                    {filteredSpots.length > 0 ? (
                      filteredSpots.map((spot) => (
                        <button
                          key={spot.uuid}
                          onClick={() => {
                            setSelectedSpotId(spot.id || spot.uuid);
                            setIsSpotDropdownOpen(false);
                            setSpotSearchQuery('');
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                            (spot.id || spot.uuid) == selectedSpotId
                              ? 'bg-theme-primary/20 text-theme-primary font-semibold'
                              : 'text-theme-text hover:bg-theme-surface/50'
                          }`}
                        >
                          {spot.spot_name}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2.5 text-sm text-theme-text-secondary text-center">
                        검색 결과가 없습니다
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* 시작일 */}
          <div className="w-full md:w-auto">
            <label className="block text-sm font-semibold text-theme-text-secondary mb-2">
              시작일
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full md:w-40 px-4 py-2.5 bg-theme-background border-2 border-theme-primary/30 rounded-xl text-sm font-medium text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all shadow-sm hover:shadow-md"
            />
          </div>

          {/* 종료일 */}
          <div className="w-full md:w-auto">
            <label className="block text-sm font-semibold text-theme-text-secondary mb-2">
              종료일
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full md:w-40 px-4 py-2.5 bg-theme-background border-2 border-theme-primary/30 rounded-xl text-sm font-medium text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all shadow-sm hover:shadow-md"
            />
          </div>

          {/* 조회 버튼 */}
          <motion.button
            onClick={() => {
              setPageIndex(0);
              refetch();
            }}
            disabled={!startDate || !endDate}
            className="w-full md:w-auto px-6 py-2.5 bg-gradient-to-r from-theme-primary to-theme-secondary text-white text-sm font-semibold rounded-xl hover:from-indigo-500 hover:to-pink-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all"
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
          >
            <HiRefresh className="w-4 h-4" />
            조회
          </motion.button>
        </div>
      </motion.div>

      {/* 다운로드 버튼 */}
      {processedData.length > 0 && (
        <motion.div
          className="flex justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.button
            onClick={handleDownloadCSV}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm font-semibold rounded-xl hover:from-green-500 hover:to-emerald-500 flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <HiDownload className="w-5 h-5" />
            CSV 다운로드
          </motion.button>
        </motion.div>
      )}

      {/* 테이블 */}
      <motion.div
        className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl border border-theme-primary/20 overflow-hidden relative z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {isLoading || isProcessingHash ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center gap-3 text-theme-text-secondary">
              <div className="w-5 h-5 border-2 border-theme-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium">
                {isLoading ? '로딩 중...' : '해시 계산 중...'}
              </span>
            </div>
          </div>
        ) : processedData.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-theme-text-secondary text-sm font-medium">
              조회 조건을 설정하고 조회 버튼을 클릭하세요.
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* 헤더 스크롤 영역 */}
            <div 
              className="overflow-x-auto overflow-y-hidden border-b-2 border-theme-primary/20"
              id="table-header-scroll"
              onScroll={(e) => {
                // 헤더 스크롤 시 바디도 함께 스크롤
                const bodyScroll = document.getElementById('table-body-scroll');
                if (bodyScroll) {
                  bodyScroll.scrollLeft = e.target.scrollLeft;
                }
              }}
            >
              <table className="divide-y divide-theme-primary/20" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                <thead className="bg-theme-surface sticky top-0 z-10">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const isGradeColumn = header.column.id === 'grade_display';
                        const isQualityFlagsColumn = header.column.id === 'quality_flags';
                        const isCenterAligned = isGradeColumn || isQualityFlagsColumn;
                        return (
                          <th
                            key={header.id}
                            className={`px-5 py-4 text-xs font-bold text-theme-text-secondary uppercase tracking-wider cursor-pointer hover:bg-theme-surface/50 transition-colors ${
                              isCenterAligned ? 'text-center' : 'text-left'
                            }`}
                            onClick={header.column.getToggleSortingHandler()}
                            style={{ 
                              width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
                              minWidth: header.column.columnDef.minSize ? `${header.column.columnDef.minSize}px` : undefined,
                              maxWidth: header.column.columnDef.maxSize ? `${header.column.columnDef.maxSize}px` : undefined,
                            }}
                          >
                            <div className={`flex items-center gap-2 ${isCenterAligned ? 'justify-center' : ''}`}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                              {{
                                asc: <span className="text-theme-primary">↑</span>,
                                desc: <span className="text-theme-primary">↓</span>,
                              }[header.column.getIsSorted()] ?? null}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
              </table>
            </div>
            
            {/* 바디 스크롤 영역 */}
            <div 
              className="overflow-x-auto overflow-y-auto"
              style={{ maxHeight: '600px' }}
              id="table-body-scroll"
              onScroll={(e) => {
                // 바디 스크롤 시 헤더도 함께 스크롤
                const headerScroll = document.getElementById('table-header-scroll');
                if (headerScroll) {
                  headerScroll.scrollLeft = e.target.scrollLeft;
                }
              }}
            >
              <table className="divide-y divide-theme-primary/20" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                <tbody className="bg-theme-background divide-y divide-theme-primary/20">
                  {table.getRowModel().rows.map((row, index) => (
                    <tr 
                      key={row.id} 
                      className={`hover:bg-theme-surface/50 transition-all ${
                        index % 2 === 0 ? 'bg-theme-background' : 'bg-theme-surface/30'
                      }`}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const isGradeColumn = cell.column.id === 'grade_display';
                        const isQualityFlagsColumn = cell.column.id === 'quality_flags';
                        const isCenterAligned = isGradeColumn || isQualityFlagsColumn;
                        return (
                          <td 
                            key={cell.id} 
                            className={`px-5 py-4 text-sm text-theme-text font-medium ${
                              isCenterAligned ? 'text-center' : ''
                            }`}
                            style={{ 
                              width: cell.column.columnDef.size ? `${cell.column.columnDef.size}px` : 'auto',
                              minWidth: cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined,
                              maxWidth: cell.column.columnDef.maxSize ? `${cell.column.columnDef.maxSize}px` : undefined,
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalCount > 0 && (
          <div className="px-6 py-4 bg-theme-surface border-t border-theme-primary/20 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm font-medium text-theme-text-secondary">
              총 <span className="font-bold text-theme-primary">{totalCount.toLocaleString()}</span>개 중{' '}
              <span className="font-semibold">
                {pageIndex * pageSize + 1}-
                {Math.min(
                  (pageIndex + 1) * pageSize,
                  totalCount
                ).toLocaleString()}
              </span>{' '}
              표시
            </div>
            <div className="flex items-center gap-3">
              <motion.button
                onClick={() => {
                  const newPageIndex = Math.max(0, pageIndex - 1);
                  setPageIndex(newPageIndex);
                }}
                disabled={pageIndex === 0}
                className="px-4 py-2 text-sm font-semibold bg-theme-background border-2 border-theme-primary/30 rounded-xl text-theme-text hover:bg-theme-surface hover:border-theme-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                이전
              </motion.button>
              <span className="text-sm font-semibold text-theme-text px-3 py-2 bg-theme-background rounded-xl border-2 border-theme-primary/30">
                {pageIndex + 1} / {Math.ceil(totalCount / pageSize)}
              </span>
              <motion.button
                onClick={() => {
                  const maxPage = Math.ceil(totalCount / pageSize) - 1;
                  const newPageIndex = Math.min(maxPage, pageIndex + 1);
                  setPageIndex(newPageIndex);
                }}
                disabled={pageIndex >= Math.ceil(totalCount / pageSize) - 1}
                className="px-4 py-2 text-sm font-semibold bg-theme-background border-2 border-theme-primary/30 rounded-xl text-theme-text hover:bg-theme-surface hover:border-theme-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                다음
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
